import { AfterViewInit, Component, ComponentRef, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSort, SortDirection } from '@angular/material/sort';
import { Observable, of, Subject } from 'rxjs';
import { EditableDataField } from '../filter/editable-data-field';
import { Card, CardSymbol } from '@deckplan.io/common';
import { MatDialog } from '@angular/material/dialog';
import { CardViewComponent, CardViewDialogData } from '../../cards/card-view/card-view.component';
import { CardSymbologyUtils } from '../../cards/card-symbology-utils';
import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ArrangableSelectorMenuComponent } from '../arrangable-selector-menu/arrangable-selector-menu.component';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { map } from 'rxjs/operators';
import { CardPickerComponent } from '../../cards/card-picker/card-picker.component';


export interface CardSwapEvent {
    oldCard: Card;
    newCard: Card;
}

export interface AvailableColumn {
    field: EditableDataField;
    selected: boolean;
}

@Component({
    selector: 'app-simple-table',
    templateUrl: './simple-table.component.html',
    styleUrls: ['./simple-table.component.scss']
})
export class SimpleTableComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() public tableId = 'Common';
    @Input() public displayedColumns: EditableDataField[] = [];
    @Input() public availableColumns: AvailableColumn[] = [];
    @Input() public tableDataContainer: any[];
    @Input() public sortColumnId: string;
    @Input() public sortDirection: SortDirection;
    @Input() public removableCards: boolean | { deckId: number } = false;
    @Input() public addCardsToDeck: boolean | { deckId: number } = true;
    @Input() public addCardsToInventory = true;
    @Input() public swappableCards = false;
    @Input() public overrideClick = false;
    @Input() public showDragHint = false;
    @Output() public cardActionTaken = new EventEmitter();

    @Output() public rowClicked = new EventEmitter<any>();
    @Output() public rowContextClicked = new EventEmitter<{ event: MouseEvent; data: any }>();
    @Output() public editableFieldChanged = new EventEmitter<{ value: any; data: any; dataField: EditableDataField }>();
    @Output() public sortChange = new Subject<any>();
    @Output() public cardSwapped = new EventEmitter<CardSwapEvent>();

    @ViewChild(MatSort, {static: true}) public sort: MatSort;
    @ViewChild('columnSelectorToggle', {static: true, read: ElementRef}) public columnSelectorToggle: ElementRef;

    public columnIds: string[];

    private columnSelectorOverlayRef: OverlayRef;
    private readonly savePrefix = 'saved-columns-';

    constructor(public readonly cardSymbologyUtils: CardSymbologyUtils,
                private readonly dialog: MatDialog,
                private readonly overlayPositionBuilder: OverlayPositionBuilder,
                private readonly overlay: Overlay) {}

    private static imgTagFromSymbol(cardSymbol: CardSymbol) {
        return `<img class="mana-symbol" alt="${cardSymbol.english}" title="${cardSymbol.english}" src="${cardSymbol.svgUri}" />`;
    }

    @Input() public isValid = (card: Card) => true;

    public ngOnInit() {
        const positionStrategy = this.overlayPositionBuilder
            .flexibleConnectedTo(this.columnSelectorToggle)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'end',
                    overlayY: 'bottom'
                }
            ]);
        this.columnSelectorOverlayRef = this.overlay.create({positionStrategy, hasBackdrop: true});
        this.columnSelectorOverlayRef.backdropClick().subscribe(() => {
            this.columnSelectorOverlayRef.detach();
        });


        // We are going to mutate the data members, we also want these mutations
        // to be contained to THIS simple table. As availableColumns is a pointer
        // to an array, we need to do a deep copy to avoid our changes propagating
        // up to the "shared" common columns
        this.availableColumns = [...this.availableColumns].map(c => ({...c}));

        if (this.availableColumns.length > 0) {
            const savedColumnsStr = localStorage.getItem(`${this.savePrefix}${this.tableId}`);
            if (savedColumnsStr) {
                const savedColumns = JSON.parse(savedColumnsStr) as any[];
                for (let i = 0; i < savedColumns.length; i++) {
                    const j = this.availableColumns.findIndex(col => col.field.field === savedColumns[i].field);
                    moveItemInArray(this.availableColumns, j, i);
                    this.availableColumns[i].selected = savedColumns[i].selected;
                }
            }

            this.displayedColumns = this.availableColumns.filter(c => c.selected).map(c => c.field);
        } else {
            this.availableColumns = this.displayedColumns.map(field => ({selected: true, field}));
        }

        this.columnIds = this.availableColumns.filter(c => c.selected).map(x => x.field.field);
    }

    public ngAfterViewInit(): void {
        this.sort.sortChange.subscribe(e => this.sortChange.next(e));
    }

    public ngOnDestroy() {
        if (this.columnSelectorOverlayRef.hasAttached()) {
            this.columnSelectorOverlayRef.detach();
        }
    }

    public onRowClicked(item: any) {
        // If the client provided an override, use that
        if (this.overrideClick) {
            this.rowClicked.emit(item);
        } else {
            const data = {
                card: item,
                removable: this.removableCards,
                addToDeck: this.addCardsToDeck,
                addToInventory: this.addCardsToInventory && !item.inventoryId,
                swappable: this.swappableCards,
                quantity: item.quantityInDeck || item.quantity
            } as CardViewDialogData;
            const dialogRef = this.dialog.open(CardViewComponent, {data, minWidth: '600px'});
            dialogRef.componentInstance.actionTaken.subscribe(() => this.cardActionTaken.emit());
            dialogRef.componentInstance.requestSwap.subscribe(() => {
                if (!item) {
                    return;
                }

                const swapDialog = this.dialog.open(CardPickerComponent, {
                    width: '100vw',
                    minWidth: '100vw',
                    height: '100vh',
                    minHeight: '100vh',
                    disableClose: false,
                    data: {
                        showOwnedCardsSelector: true,
                        query: {
                            operator: 'and', operands: [
                                {dataField: {field: 'name', title: 'Name', dataType: 'string'}, operation: '=', value: item.name},
                                {dataField: {field: 'id', title: 'Name', dataType: 'string'}, operation: '!=', value: item.id}
                            ]
                        },
                        override: true
                    }
                });
                swapDialog.afterClosed().subscribe(card => {
                    card.quantity = item.quantity;
                    if (card) {
                        this.cardSwapped.emit({oldCard: item, newCard: card});
                        item = card;
                        dialogRef.componentInstance.card = item;
                    }
                });
            });
        }
    }

    public onRowContext(mouseEvent: MouseEvent, item: any) {
        // Stop default context menu from showing
        mouseEvent.stopPropagation();
        this.rowContextClicked.emit({event: mouseEvent, data: item});
        return false;
    }

    public onInputChanged(inputValue: any, item: any, modifiedDataField: EditableDataField) {
        this.editableFieldChanged.emit({value: inputValue, data: item, dataField: modifiedDataField});
    }

    public arrangeColumns() {
        const columnPickerPortal = new ComponentPortal(ArrangableSelectorMenuComponent);

        const menuRef: ComponentRef<ArrangableSelectorMenuComponent> = this.columnSelectorOverlayRef.attach(columnPickerPortal);

        const updateColumns = (ev) => {
            this.displayedColumns = this.availableColumns.filter(c => c.selected).map(c => c.field);
            this.columnIds = this.availableColumns.filter(c => c.selected).map(x => x.field.field);
            localStorage.setItem(`${this.savePrefix}${this.tableId}`, JSON.stringify(this.availableColumns.map(c => ({selected: c.selected, field: c.field.field}))));
        };

        menuRef.instance.menuOptions = this.availableColumns.map(col => ({option: col.field.title, selected: col.selected}));
        menuRef.instance.optionToggled.subscribe(ev => {
            const col = this.availableColumns.find(c => c.field.title === ev.option);
            col.selected = ev.selected;
            updateColumns(ev);
        });
        menuRef.instance.optionMoved.subscribe(ev => {
            moveItemInArray(this.availableColumns, ev.prevIndex, ev.currIndex);
            updateColumns(ev);
        });
    }

    public getCardText(card: any): Observable<string> {
        if (!card) {
            return of('');
        }

        let text = '';

        if (card.oracleText) {
            text = card.oracleText;
        }

        if (card.cardFaces && card.cardFaces.length > 0) {
            text = card.cardFaces[0].oracle_text;
        }

        return this.parseSymbols(text);
    }

    private parseSymbols(text: string): Observable<string> {
        const pattern = /({.+?})/g;
        // @ts-ignore String.matchAll is part of es2020
        const symbols = [...text.matchAll(pattern)].map(match => match[0]);

        if (symbols.length === 0) {
            return of(text);
        }

        return this.cardSymbologyUtils.getSymbols(symbols).pipe(
            map(cardSymbols => {
                const images = cardSymbols.map(cardSymbol => SimpleTableComponent.imgTagFromSymbol(cardSymbol));
                for (let i = 0; i < symbols.length; i++) {
                    text = text.replace(symbols[i], images[i]);
                }
                return text;
            })
        );
    }

    public isSkinnyColumn(column: EditableDataField) {
        return column.dataType === 'boolean' || column.dataType === 'cardset' || column.field === 'cmc' || column.field === 'quantity' || column.field === 'quantityInDeck';
    }

    public isMedSkinnyColumn(column: EditableDataField) {
        return column.dataType === 'mana' || column.dataType === 'color' || column.dataType === 'rarity';
    }

    public isLargeSkinnyColumn(column: EditableDataField) {
        return column.dataType === 'quality';
    }
}
